import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import {
  Plus,
  Pin,
  Lock,
  ShoppingCart,
  ListChecks,
  ListTodo,
  ChevronRight,
  ChevronLeft,
  Trash2,
  MoreVertical,
  UserPlus,
  Hand,
  Clock,
  Pencil,
  X,
} from 'lucide-react'
import { formatPoints, type ListDto, type ListItemDto, type ListType, type MemberDto } from '@familynest/shared'
import { api } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { useLive } from '../../lib/ws'
import { Avatar, Button, ErrorBanner, useBackdropClose } from '../ui'
import { useDialogs } from '../ui/dialogs'

const TYPE_ICON: Record<ListType, typeof ShoppingCart> = {
  shopping: ShoppingCart,
  todo: ListTodo,
  custom: ListChecks,
}

export function ListsScreen({ members }: { members: MemberDto[] }): React.JSX.Element {
  const { member } = useAuth()
  const dialogs = useDialogs()
  const [lists, setLists] = useState<ListDto[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members])

  const loadLists = useCallback(async () => {
    const ls = await api.listLists()
    setLists(ls)
    // Mobil (Android/Handy) mit der Übersicht starten; nur auf Desktop die erste
    // Liste vorwählen (Master/Detail nebeneinander).
    setSelectedId((cur) => {
      if (cur) return cur
      const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
      return isDesktop ? (ls[0]?.id ?? null) : null
    })
  }, [])

  useEffect(() => {
    void loadLists()
  }, [loadLists])
  useLive('lists', loadLists)

  const selected = lists.find((l) => l.id === selectedId) ?? null
  const canAddList = member?.role !== 'guest'

  async function addList(): Promise<void> {
    const name = (await dialogs.prompt({ title: 'Neue Liste', placeholder: 'Name der Liste' }))?.trim()
    if (!name) return
    const created = await api.createList({ name })
    await loadLists()
    setSelectedId(created.id)
  }

  return (
    <div className="grid h-full grid-cols-1 md:grid-cols-[20rem_1fr]">
      {/* Master */}
      <aside
        className={`flex-col border-r ${selected ? 'hidden md:flex' : 'flex'}`}
        style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>
            Listen
          </h1>
          {canAddList && (
            <Button onClick={addList}>
              <Plus className="size-4" /> Neu
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-auto px-2 pb-2">
          {lists.map((l) => (
            <MasterRow key={l.id} list={l} active={l.id === selectedId} onClick={() => setSelectedId(l.id)} />
          ))}
          {lists.length === 0 && (
            <p className="px-3 py-6 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
              Noch keine Listen.
            </p>
          )}
        </div>
      </aside>

      {/* Detail */}
      <section className={`min-h-0 ${selected ? 'flex' : 'hidden md:flex'} flex-col`}>
        {selected && member ? (
          <ListDetail
            key={selected.id}
            list={selected}
            member={member}
            members={memberMap}
            onBack={() => setSelectedId(null)}
            onChanged={loadLists}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
            Wähle links eine Liste.
          </div>
        )}
      </section>
    </div>
  )
}

function MasterRow({ list, active, onClick }: { list: ListDto; active: boolean; onClick: () => void }): React.JSX.Element {
  const Icon = TYPE_ICON[list.type]
  const total = list.openCount + list.doneCount
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-black/5"
      style={active ? { backgroundColor: 'var(--color-accent)' + '18' } : undefined}
    >
      <Icon className="size-5 shrink-0" style={{ color: 'var(--color-accent)' }} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          {list.isPinned && <Pin className="size-3.5" style={{ color: 'var(--color-muted)' }} />}
          {list.isPrivate && <Lock className="size-3.5" style={{ color: 'var(--color-muted)' }} />}
          <span className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>
            {list.name}
          </span>
        </span>
        <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {total === 0 ? 'leer' : `${list.doneCount}/${total} erledigt`}
        </span>
      </span>
      <ChevronRight className="size-4 shrink-0 md:hidden" style={{ color: 'var(--color-muted)' }} />
    </button>
  )
}

function ListDetail({
  list,
  member,
  members,
  onBack,
  onChanged,
}: {
  list: ListDto
  member: MemberDto
  members: Map<string, MemberDto>
  onBack: () => void
  onChanged: () => void
}): React.JSX.Element {
  const dialogs = useDialogs()
  const [items, setItems] = useState<ListItemDto[]>([])
  const [draft, setDraft] = useState('')
  const [showDone, setShowDone] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [editItem, setEditItem] = useState<ListItemDto | null>(null)

  const canModify = member.role === 'adult' || (member.role === 'child' && list.ownerMemberId === member.id)
  const canDelete = member.role === 'adult'
  const canEditItems = member.role !== 'guest'

  const [loadError, setLoadError] = useState(false)
  const load = useCallback(async () => {
    try {
      setItems(await api.listItems(list.id))
      setLoadError(false)
    } catch {
      setLoadError(true)
    }
  }, [list.id])
  useEffect(() => {
    void load()
  }, [load])
  useLive('lists', load)

  const open = items.filter((i) => i.checkedAt === null)
  const done = items.filter((i) => i.checkedAt !== null)

  async function add(): Promise<void> {
    const text = draft.trim()
    if (!text) return
    setDraft('')
    await api.addItem(list.id, { text })
    await load()
    onChanged()
  }
  async function update(item: ListItemDto, patch: Parameters<typeof api.updateItem>[1]): Promise<void> {
    await api.updateItem(item.id, patch)
    await load()
    onChanged()
  }
  async function removeItem(item: ListItemDto): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Eintrag löschen', message: `„${item.text}" löschen?`, confirmLabel: 'Löschen', danger: true }))) return
    await api.deleteItem(item.id)
    await load()
    onChanged()
  }
  async function assign(item: ListItemDto, memberId: string | null): Promise<void> {
    await api.updateItem(item.id, { assignedTo: memberId })
    await load()
    onChanged()
  }
  const childMembers = useMemo(() => [...members.values()].filter((x) => x.role === 'child'), [members])
  async function onKey(e: KeyboardEvent<HTMLInputElement>): Promise<void> {
    if (e.key === 'Enter') await add()
  }
  async function togglePin(): Promise<void> {
    setMenuOpen(false)
    await api.updateList(list.id, { isPinned: !list.isPinned })
    onChanged()
  }
  async function rename(): Promise<void> {
    setMenuOpen(false)
    const name = (await dialogs.prompt({ title: 'Liste umbenennen', defaultValue: list.name }))?.trim()
    if (name) {
      await api.updateList(list.id, { name })
      onChanged()
    }
  }
  async function remove(): Promise<void> {
    setMenuOpen(false)
    if (await dialogs.confirm({ title: 'Liste löschen', message: `Liste „${list.name}" löschen?`, confirmLabel: 'Löschen', danger: true })) {
      await api.deleteList(list.id)
      onBack()
      onChanged()
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Kopf */}
      <div className="flex items-center gap-2 border-b px-3 py-3" style={{ borderColor: 'var(--color-line)' }}>
        <button onClick={onBack} className="rounded-lg p-1 hover:bg-black/5 md:hidden" aria-label="Zurück">
          <ChevronLeft className="size-5" style={{ color: 'var(--color-ink)' }} />
        </button>
        <h2 className="flex items-center gap-2 text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>
          {list.isPrivate && <Lock className="size-4" style={{ color: 'var(--color-muted)' }} />}
          {list.name}
        </h2>
        <span className="text-sm" style={{ color: 'var(--color-muted)' }}>
          {list.doneCount}/{list.openCount + list.doneCount}
        </span>
        {canModify && (
          <div className="relative ml-auto">
            <button onClick={() => setMenuOpen((v) => !v)} className="rounded-lg p-1.5 hover:bg-black/5" aria-label="Menü">
              <MoreVertical className="size-5" style={{ color: 'var(--color-muted)' }} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-9 z-10 flex w-44 flex-col rounded-lg border py-1 shadow-lg"
                style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}
              >
                <MenuItem onClick={togglePin}>
                  <Pin className="size-4" /> {list.isPinned ? 'Nicht mehr anheften' : 'Anheften'}
                </MenuItem>
                <MenuItem onClick={rename}>Umbenennen</MenuItem>
                {canDelete && (
                  <MenuItem onClick={remove} danger>
                    <Trash2 className="size-4" /> Löschen
                  </MenuItem>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schnell-Eingabe */}
      <div className="px-3 py-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder="Hinzufügen…  ⏎"
          className="w-full rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]"
          style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }}
        />
      </div>

      {loadError && <ErrorBanner message="Liste konnte nicht geladen werden." onRetry={() => void load()} />}

      {/* Items */}
      <div className="flex-1 overflow-auto px-3 pb-4">
        {open.length === 0 && done.length === 0 && (
          <p className="py-10 text-center text-sm" style={{ color: 'var(--color-muted)' }}>
            Nichts zu tun — oder fehlt was? Tipp es einfach oben ein.
          </p>
        )}
        <ul className="flex flex-col">
          {open.map((i) => (
            <ItemRow key={i.id} item={i} me={member} listType={list.type} members={members} childMembers={childMembers} canEdit={canEditItems} onUpdate={(patch) => update(i, patch)} onEdit={() => setEditItem(i)} onDelete={() => removeItem(i)} onAssign={(mid) => assign(i, mid)} />
          ))}
        </ul>

        {done.length > 0 && (
          <div className="mt-3">
            <button onClick={() => setShowDone((v) => !v)} className="flex items-center gap-1 px-1 py-1 text-sm" style={{ color: 'var(--color-muted)' }}>
              <ChevronRight className={`size-4 transition-transform ${showDone ? 'rotate-90' : ''}`} />
              Erledigt ({done.length})
            </button>
            {showDone && (
              <ul className="flex flex-col opacity-70">
                {done.map((i) => (
                  <ItemRow key={i.id} item={i} me={member} listType={list.type} members={members} childMembers={childMembers} canEdit={canEditItems} onUpdate={(patch) => update(i, patch)} onEdit={() => setEditItem(i)} onDelete={() => removeItem(i)} onAssign={(mid) => assign(i, mid)} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {editItem && (
        <EditItemDialog
          item={editItem}
          listType={list.type}
          isAdult={member.role === 'adult'}
          onClose={() => setEditItem(null)}
          onSaved={async (patch) => { await update(editItem, patch); setEditItem(null) }}
        />
      )}
    </div>
  )
}

function EditItemDialog({
  item,
  listType,
  isAdult,
  onClose,
  onSaved,
}: {
  item: ListItemDto
  listType: ListType
  isAdult: boolean
  onClose: () => void
  onSaved: (patch: { text: string; note: string | null; rewardAmount?: number | null }) => Promise<void>
}): React.JSX.Element {
  const [text, setText] = useState(item.text)
  const [note, setNote] = useState(item.note ?? '')
  const [reward, setReward] = useState(item.rewardAmount ? String(item.rewardAmount) : '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const showReward = listType === 'todo' && isAdult
  const field = 'w-full rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]'
  const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const

  async function save(): Promise<void> {
    const t = text.trim()
    if (!t) { setError('Bitte einen Text eingeben.'); return }
    // Punkte prüfen → ganze Zahl (1 P = 1 Cent).
    let rewardPoints: number | null = null
    if (showReward && reward.trim()) {
      const n = Number(reward.trim())
      if (!Number.isInteger(n) || n < 0) {
        setError('Bitte ganze Punkte eingeben (z. B. 50).')
        return
      }
      rewardPoints = n
    }
    setBusy(true)
    setError(null)
    try {
      const patch: { text: string; note: string | null; rewardAmount?: number | null } = { text: t, note: note.trim() || null }
      if (showReward) patch.rewardAmount = rewardPoints
      await onSaved(patch)
    } catch {
      setError('Speichern fehlgeschlagen. Bitte Eingaben prüfen.')
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-sm overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Aufgabe bearbeiten</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <div className="flex flex-col gap-3">
          <input className={field} style={fieldStyle} value={text} onChange={(e) => setText(e.target.value)} placeholder="Aufgabe" autoFocus />
          <input className={field} style={fieldStyle} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz (optional)" />
          {showReward && (
            <label className="flex flex-col gap-1 text-sm">
              <span style={{ color: 'var(--color-muted)' }}>Punkte bei Erledigung (optional · 100 P = 1 €)</span>
              <input className={field} style={fieldStyle} inputMode="numeric" value={reward} onChange={(e) => setReward(e.target.value)} placeholder="z. B. 50" />
            </label>
          )}
          {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
            <Button onClick={save} disabled={busy || !text.trim()}>Speichern</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ItemRow({
  item,
  me,
  listType,
  members,
  childMembers,
  canEdit,
  onUpdate,
  onEdit,
  onDelete,
  onAssign,
}: {
  item: ListItemDto
  me: MemberDto
  listType: ListType
  members: Map<string, MemberDto>
  childMembers: MemberDto[]
  canEdit: boolean
  onUpdate: (patch: { checked?: boolean; reportDone?: boolean }) => void
  onEdit: () => void
  onDelete: () => void
  onAssign: (memberId: string | null) => void
}): React.JSX.Element {
  const assignee = item.assignedTo ? members.get(item.assignedTo) : null
  const isTodo = listType === 'todo'
  const confirmed = item.checkedAt !== null
  const reported = item.reportedDoneAt !== null && !confirmed
  const iAmAssignee = me.id === item.assignedTo

  // Zwei-Schritt-Flow (ADR-030/076): Auf Aufgaben-Listen MELDEN Kinder nur, BESTÄTIGEN
  // dürfen ausschließlich Erwachsene — auch bei unbelegten Aufgaben. Ein Kind kann die
  // eigene ODER eine unbelegte Aufgabe melden (unbelegte wird serverseitig angenommen).
  const childMayReport = isTodo && me.role === 'child' && (item.assignedTo === null || iAmAssignee)

  const boxDisabled = isTodo ? me.role === 'guest' || (me.role === 'child' && (confirmed || !childMayReport)) : false
  function onBox(): void {
    if (!isTodo) return onUpdate({ checked: !confirmed })
    if (me.role === 'adult') return onUpdate({ checked: !confirmed }) // bestätigen / wieder öffnen / direkt erledigen
    if (childMayReport && !confirmed) onUpdate({ reportDone: !reported }) // melden / zurückziehen
  }

  const reporter = item.reportedBy ? members.get(item.reportedBy) : null
  const boxColor = confirmed ? 'var(--color-success)' : reported ? '#c48a2b' : 'var(--color-line)'
  const overdue = !!item.dueAt && !confirmed && new Date(item.dueAt).getTime() < Date.now()

  return (
    <li className="group flex items-start gap-3 border-b py-2" style={{ borderColor: 'var(--color-line)' }}>
      <button
        onClick={onBox}
        disabled={boxDisabled}
        aria-label={confirmed ? 'Als offen markieren' : reported ? 'Meldung' : 'Als erledigt markieren'}
        className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border disabled:cursor-default"
        style={{ borderColor: boxColor, backgroundColor: confirmed ? 'var(--color-success)' : 'transparent' }}
      >
        {confirmed && (
          <svg viewBox="0 0 12 12" className="size-3.5" fill="none" stroke="#fff" strokeWidth="2">
            <path d="M2 6l3 3 5-6" />
          </svg>
        )}
        {reported && <Clock className="size-3" style={{ color: '#c48a2b' }} />}
      </button>
      <span className="min-w-0 flex-1">
        <span className={`block break-words ${confirmed ? 'line-through' : ''}`} style={{ color: confirmed ? 'var(--color-muted)' : 'var(--color-ink)' }}>
          {item.text}
          {item.quantity && <span className="ml-2 text-sm" style={{ color: 'var(--color-muted)' }}>{item.quantity}</span>}
        </span>
        {reported && (
          <span className="block text-xs" style={{ color: '#c48a2b' }}>
            {me.role === 'adult'
              ? `${reporter?.name ?? 'Kind'} meldet erledigt — bitte bestätigen`
              : 'erledigt gemeldet · wartet auf Bestätigung'}
          </span>
        )}
        {listType === 'shopping' && confirmed && item.checkedAt && (
          <span className="block text-xs" style={{ color: 'var(--color-muted)' }}>
            erledigt durch {(item.checkedBy ? members.get(item.checkedBy)?.name : null) ?? 'jemand'} am{' '}
            {new Date(item.checkedAt).toLocaleDateString('de-DE')} um {new Date(item.checkedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        {isTodo && item.dueAt && !confirmed && (
          <span className="block text-xs" style={{ color: overdue ? 'var(--color-danger)' : 'var(--color-muted)' }}>
            {overdue ? '⏰ überfällig · war fällig ' : 'zu erledigen bis '}
            {new Date(item.dueAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}, {new Date(item.dueAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
          </span>
        )}
      </span>
      {/* Aktionen: dürfen umbrechen statt den Titel zu quetschen. */}
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5" style={{ maxWidth: '45%' }}>
        {isTodo && item.rewardAmount ? (
          <span className="rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-accent)' }}>
            +{formatPoints(item.rewardAmount)}
          </span>
        ) : null}
        {reported && me.role === 'adult' && (
          <button
            onClick={() => onUpdate({ checked: true })}
            className="rounded-full px-2.5 py-1 text-xs font-medium"
            style={{ backgroundColor: 'var(--color-success)', color: '#fff' }}
          >
            Bestätigen
          </button>
        )}
        {/* Kind: klarer „Fertig melden"-Button (statt nur der Checkbox). */}
        {childMayReport && !confirmed && (
          reported ? (
            <button
              onClick={() => onUpdate({ reportDone: false })}
              className="rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-muted)', border: '1px solid var(--color-line)' }}
            >
              Zurückziehen
            </button>
          ) : (
            <button
              onClick={() => onUpdate({ reportDone: true })}
              className="rounded-full px-2.5 py-1 text-xs font-medium"
              style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
            >
              Fertig melden
            </button>
          )
        )}
        {isTodo ? (
          <AssignControl item={item} me={me} assignee={assignee ?? null} childMembers={childMembers} onAssign={onAssign} />
        ) : (
          assignee && <Avatar name={assignee.name} color={assignee.color} size={22} src={assignee.avatarUrl} />
        )}
        {me.role === 'adult' && (
          <button onClick={onEdit} aria-label="Bearbeiten" className="rounded p-1 hover:bg-black/5 sm:opacity-0 sm:group-hover:opacity-100">
            <Pencil className="size-4" style={{ color: 'var(--color-muted)' }} />
          </button>
        )}
        {canEdit && (
          <button onClick={onDelete} aria-label="Löschen" className="rounded p-1 hover:bg-black/5 sm:opacity-0 sm:group-hover:opacity-100">
            <Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} />
          </button>
        )}
      </div>
    </li>
  )
}

/**
 * Zuweisung/Annahme einer Aufgabe. Eltern weisen aus der Kinderliste zu (oder
 * „Niemand"). Kinder können eine **unbelegte** Aufgabe „Annehmen" und ihre
 * eigene wieder abgeben. Gast: nur Anzeige.
 */
function AssignControl({
  item,
  me,
  assignee,
  childMembers,
  onAssign,
}: {
  item: ListItemDto
  me: MemberDto
  assignee: MemberDto | null
  childMembers: MemberDto[]
  onAssign: (memberId: string | null) => void
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false)

  // Kind
  if (me.role === 'child') {
    if (!item.assignedTo) {
      return (
        <button
          onClick={() => onAssign(me.id)}
          className="flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
          style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
        >
          <Hand className="size-3.5" /> Annehmen
        </button>
      )
    }
    if (item.assignedTo === me.id) {
      return (
        <button
          onClick={() => onAssign(null)}
          title="Aufgabe wieder abgeben"
          className="flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs"
          style={{ border: '1px solid var(--color-line)', color: 'var(--color-muted)' }}
        >
          <Avatar name={me.name} color={me.color} size={18} src={me.avatarUrl} /> meine Aufgabe
        </button>
      )
    }
    return assignee ? <Avatar name={assignee.name} color={assignee.color} size={22} src={assignee.avatarUrl} /> : null
  }

  // Gast: nur Anzeige
  if (me.role === 'guest') {
    return assignee ? <Avatar name={assignee.name} color={assignee.color} size={22} src={assignee.avatarUrl} /> : null
  }

  // Erwachsener: zuweisen
  return (
    <div className="relative shrink-0">
      <button onClick={() => setOpen((v) => !v)} className="flex items-center rounded-full p-0.5 hover:bg-black/5" aria-label="Zuweisen">
        {assignee ? (
          <Avatar name={assignee.name} color={assignee.color} size={22} src={assignee.avatarUrl} />
        ) : (
          <span className="flex size-6 items-center justify-center rounded-full border border-dashed" style={{ borderColor: 'var(--color-line)' }}>
            <UserPlus className="size-3.5" style={{ color: 'var(--color-muted)' }} />
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-10 flex w-44 flex-col rounded-lg border py-1 shadow-lg" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
          {childMembers.length === 0 && <span className="px-3 py-2 text-xs" style={{ color: 'var(--color-muted)' }}>Keine Kinder angelegt.</span>}
          {childMembers.map((c) => (
            <button
              key={c.id}
              onClick={() => { onAssign(c.id); setOpen(false) }}
              className="flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5"
              style={{ color: 'var(--color-ink)' }}
            >
              <Avatar name={c.name} color={c.color} size={20} src={c.avatarUrl} /> {c.name}
              {item.assignedTo === c.id && <span className="ml-auto text-xs" style={{ color: 'var(--color-accent)' }}>✓</span>}
            </button>
          ))}
          {item.assignedTo && (
            <button onClick={() => { onAssign(null); setOpen(false) }} className="flex items-center gap-2 border-t px-3 py-2 text-left text-sm hover:bg-black/5" style={{ borderColor: 'var(--color-line)', color: 'var(--color-muted)' }}>
              Zuweisung entfernen
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function MenuItem({ children, onClick, danger = false }: { children: React.ReactNode; onClick: () => void; danger?: boolean }): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5"
      style={{ color: danger ? 'var(--color-danger)' : 'var(--color-ink)' }}
    >
      {children}
    </button>
  )
}
